import { useEffect, useState } from "react";
import { api, formatApiError } from "../api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import ImagePicker from "../components/ImagePicker";
import { useSiteSettings } from "../../contexts/SiteSettings";

const ColorField = ({ label, value, onChange, testId }) => (
  <div>
    <Label>{label}</Label>
    <div className="flex items-center gap-3 mt-1">
      <div
        className="w-10 h-10 rounded-md border border-border"
        style={{ backgroundColor: `hsl(${value})` }}
      />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. 9 70% 62%"
        data-testid={testId}
      />
    </div>
    <p className="text-xs text-muted-foreground mt-1">HSL format: hue saturation% lightness%</p>
  </div>
);

export default function SiteSettingsPage() {
  const { refresh } = useSiteSettings();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/api/admin/settings")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { key, updated_at, ...payload } = data;
      const { data: updated } = await api.put("/api/admin/settings", payload);
      setData(updated);
      await refresh();
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!data)
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );

  return (
    <div className="max-w-4xl space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif font-bold">Site Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">
            These changes apply across the entire public site instantly.
          </p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary-dark text-white" data-testid="save-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save changes
        </Button>
      </div>

      <Card className="p-6 space-y-5">
        <h2 className="font-serif font-semibold text-lg">Branding</h2>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Label>Website name</Label>
            <Input value={data.site_name || ""} onChange={(e) => set("site_name", e.target.value)} data-testid="site-name-input" />
          </div>
          <div>
            <Label>Tagline</Label>
            <Input value={data.tagline || ""} onChange={(e) => set("tagline", e.target.value)} />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <ImagePicker label="Logo" value={data.logo_url} onChange={(v) => set("logo_url", v)} testId="logo-picker" />
          <ImagePicker label="Favicon" value={data.favicon_url} onChange={(v) => set("favicon_url", v)} testId="favicon-picker" />
        </div>
      </Card>

      <Card className="p-6 space-y-5">
        <h2 className="font-serif font-semibold text-lg">Color theme</h2>
        <div className="grid md:grid-cols-3 gap-5">
          <ColorField label="Primary" value={data.primary_color} onChange={(v) => set("primary_color", v)} testId="primary-color" />
          <ColorField label="Secondary" value={data.secondary_color} onChange={(v) => set("secondary_color", v)} />
          <ColorField label="Accent" value={data.accent_color} onChange={(v) => set("accent_color", v)} />
        </div>
      </Card>

      <Card className="p-6 space-y-5">
        <h2 className="font-serif font-semibold text-lg">Contact details</h2>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Label>Email</Label>
            <Input value={data.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={data.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} />
          </div>
          <div>
            <Label>Address</Label>
            <Input value={data.address || ""} onChange={(e) => set("address", e.target.value)} />
          </div>
          <div>
            <Label>Office hours</Label>
            <Input value={data.office_hours || ""} onChange={(e) => set("office_hours", e.target.value)} />
          </div>
        </div>
      </Card>

      <Card className="p-6 space-y-5">
        <h2 className="font-serif font-semibold text-lg">Social media</h2>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Label>Facebook URL</Label>
            <Input value={data.facebook_url || ""} onChange={(e) => set("facebook_url", e.target.value)} />
          </div>
          <div>
            <Label>Instagram URL</Label>
            <Input value={data.instagram_url || ""} onChange={(e) => set("instagram_url", e.target.value)} />
          </div>
          <div>
            <Label>X (Twitter) URL</Label>
            <Input value={data.twitter_url || ""} onChange={(e) => set("twitter_url", e.target.value)} />
          </div>
          <div>
            <Label>WhatsApp URL</Label>
            <Input value={data.whatsapp_url || ""} onChange={(e) => set("whatsapp_url", e.target.value)} />
          </div>
        </div>
        <div>
          <Label>Footer copyright text</Label>
          <Textarea
            rows={2}
            value={data.footer_copyright || ""}
            onChange={(e) => set("footer_copyright", e.target.value)}
          />
        </div>
      </Card>
    </div>
  );
}
